import { useCallback, useEffect, useState } from 'react'

import { Search as SearchIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography
} from '@mui/material'

import LocationCard from './components/LocationCard/LocationCard.component'

import { getUspsLocationsApi } from '../../../redux/states/usps/getUspsLocations/getUspsLocations.api'
import { UspsLocation } from '../../../redux/states/usps/getUspsLocations/getUspsLocations.types'
import { defaultRadiusMiles, getCacheKey, locationsCache } from './USPSLocationFinder.functions'
import styles from './USPSLocationFinder.styles'
import { USPSLocationFinderProps } from './USPSLocationFinder.types'

export default function USPSLocationFinder({
  recipientZipCode,
  onLocationSelect,
  selectedLocationID,
  hideSearchForm = false,
  initialZipCode,
  radiusMiles: radiusMilesProp,
  sectionTitle,
  disabledLocationIDs
}: USPSLocationFinderProps) {
  const effectiveZip = initialZipCode ?? recipientZipCode
  const radiusMiles = radiusMilesProp ?? defaultRadiusMiles()
  const [zipCode, setZipCode] = useState(effectiveZip || recipientZipCode)
  const [radius, setRadius] = useState(radiusMiles)
  const [locations, setLocations] = useState<UspsLocation[]>([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)

  const handleSearch = useCallback(
    async (zipToUse?: string, radiusToUse?: number) => {
      const zip = zipToUse ?? zipCode
      const rad = radiusToUse ?? radius

      if (!/^\d{5}$/.test(zip)) {
        return
      }

      const cacheKey = getCacheKey(zip, rad)
      const cached = locationsCache.get(cacheKey)

      if (cached) {
        setLocations(cached.locations)
        setSearched(true)
        setLoading(false)

        return
      }

      setLoading(true)
      setSearched(false)

      try {
        const response = await getUspsLocationsApi({ zipCode: zip, radius: rad })
        locationsCache.set(cacheKey, { locations: response.data.locations, errorMessage: null })
        setLocations(response.data.locations)
        setSearched(true)
      } finally {
        setLoading(false)
      }
    },
    [zipCode, radius]
  )

  useEffect(() => {
    if (hideSearchForm && effectiveZip && /^\d{5}$/.test(effectiveZip)) {
      handleSearch(effectiveZip, radiusMiles)
    }
  }, [hideSearchForm, effectiveZip, radiusMiles, handleSearch])

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <Box id="usps-locations-section" sx={styles.container}>
      {!hideSearchForm && (
        <Card variant="outlined" sx={styles.searchCard}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Find USPS Drop-off Location
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Search for nearby USPS post offices where you can drop off your package
            </Typography>

            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  onKeyPress={handleKeyPress}
                  inputProps={{ maxLength: 5 }}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Radius</InputLabel>

                  <Select value={radius} label="Radius" onChange={(e) => setRadius(Number(e.target.value))}>
                    <MenuItem value={5}>5 miles</MenuItem>
                    <MenuItem value={10}>10 miles</MenuItem>
                    <MenuItem value={15}>15 miles</MenuItem>
                    <MenuItem value={25}>25 miles</MenuItem>
                    <MenuItem value={50}>50 miles</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleSearch()}
                  disabled={loading || !zipCode}
                  startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                >
                  {loading ? 'Searching...' : 'Find Locations'}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {hideSearchForm && loading && (
        <Box sx={styles.skeletonContainer}>
          <Skeleton variant="rounded" height={24} width="40%" sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={100} width="100%" />
          <Skeleton variant="rounded" height={100} width="100%" sx={{ mt: 2 }} />
          <Skeleton variant="rounded" height={100} width="100%" sx={{ mt: 1 }} />
        </Box>
      )}

      {(searched || (hideSearchForm && locations.length > 0)) && !loading && locations.length > 0 && (
        <Box>
          <Typography variant="h6" sx={styles.sectionTitle} gutterBottom>
            {sectionTitle ?? `${locations.length} location${locations.length !== 1 ? 's' : ''} found`}
          </Typography>

          <Box sx={styles.locationsList}>
            {locations.map((location) => {
              const isDisabled = disabledLocationIDs?.has(location.locationID) ?? false

              return (
                <LocationCard
                  key={location.locationID}
                  location={location}
                  isSelected={location.locationID === selectedLocationID}
                  isDisabled={isDisabled}
                  onSelect={() => {
                    if (!isDisabled) {
                      onLocationSelect(location.locationID, location)
                    }
                  }}
                />
              )
            })}
          </Box>
        </Box>
      )}
    </Box>
  )
}
